import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getMonthlyGoals,
  getDashboardStats,
  getCommissionLeaderboard,
  getSoldPropertyCommissions,
} from '../../../api/kpi';
import { getAgents } from '../../../api/agent';
import { apiCache, cacheKeys, cachedApiCall } from '../../../lib/cache';
import toast from 'react-hot-toast';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { hasAdminAccess } from "../../../utils/roleAccess";
import AgentSignupPanel from "../../analytics/components/AgentSignupPanel";

/*
 * Leaderboard (formerly "Commissions") — an office TV-style dashboard with two views:
 *  1. Agency leaderboard — office-wide outcome cards, an agent ranking table, a
 *     results breakdown and the recent-settlements table.
 *  2. My stats — the signed-in agent's own KPI & Goals data (the exact same
 *     MonthlyGoal figures shown on the "KPI & Goals" page): outcome cards, the
 *     GCI target bar, the current-progress grid and a historical trend chart.
 *
 * Structure mirrors the approved mockup (collings_tv_dashboard_v5.html). Every
 * figure is wired to real data — nothing is hard-coded.
 */

// Brand palette lifted from the mockup so the two match visually. Applied via
// inline styles, so the values are CSS variables (light values in index.css,
// dark values in styles/theme-dark.css) rather than literal hexes.
const C = {
  teal: '#55C5D0',
  darkGreen: 'var(--m-dark-green)',
  green: 'var(--m-green)',
  greenLight: 'var(--m-green-light)',
  greenBar: 'var(--m-green-bar)',
  amber: 'var(--m-amber)',
  red: 'var(--m-red)',
  redLight: 'var(--m-red-light)',
  surface1: 'var(--m-surface)',
  border: 'var(--m-border)',
  textPrimary: 'var(--m-ink)',
  textSecondary: 'var(--m-ink-2)',
  textMuted: 'var(--m-ink-3)',
};

// KPI metrics — identical set + order to the "KPI & Goals" page. `auto` marks
// metrics the backend computes from real CRM activity. `fields` maps each metric
// to its MonthlyGoal [current, target] field names.
const METRICS = [
  { id: 'calls_made', label: 'Call Connects', unit: 'calls', auto: true, fields: ['current_calls', 'calls_target'] },
  { id: 'text_sent', label: 'Text Sent', unit: 'texts', auto: false, fields: ['current_text', 'text_target'] },
  { id: 'email_sent', label: 'Email Sent', unit: 'emails', auto: false, fields: ['current_email', 'email_target'] },
  { id: 'door_knocks', label: 'Door Knocks', unit: 'knocks', auto: false, fields: ['current_door_knocks', 'door_knocks_target'] },
  { id: 'letter_drops', label: 'Letter Drops', unit: 'letters', auto: false, fields: ['current_letter_drops', 'letter_drops_target'] },
  { id: 'social_media', label: 'Social Media', unit: 'posts', auto: false, fields: ['current_social_media', 'social_media_target'] },
  { id: 'appraisals_completed', label: 'Appraisals Completed', unit: 'appraisals', auto: true, fields: ['current_appraisal', 'appraisal_target'] },
  { id: 'listings_won', label: 'Listings Won', unit: 'listings', auto: true, fields: ['current_listing', 'listing_target'] },
  { id: 'sales_closed', label: 'Sales Closed', unit: 'sales', auto: true, fields: ['current_sales', 'sales_target'] },
];

// Quick trailing-window ranges (end today, look back N weeks/months). Week
// windows are exact trailing days; month windows snap to the 1st (see periodRange).
const QUICK_RANGES = [
  { key: '1w', label: '1W', days: 7 },
  { key: '2w', label: '2W', days: 14 },
  { key: '1m', label: '1M', months: 1 },
  { key: '3m', label: '3M', months: 3 },
  { key: '6m', label: '6M', months: 6 },
  { key: '12m', label: '12M', months: 12 },
];

// Australian financial year runs 1 Jul → 30 Jun. `fyStartYear` returns the year
// the FY *starts* in for a given date (7 Jul 2026 → 2026, i.e. FY 2026-27).
const fyStartYear = (d) => (d.getMonth() + 1 >= 7 ? d.getFullYear() : d.getFullYear() - 1);
const fyLabel = (startYear) => `FY ${String(startYear).slice(-2)}-${String(startYear + 1).slice(-2)}`;
const fyRange = (startYear) => ({ start_date: `${startYear}-07-01`, end_date: `${startYear + 1}-06-30` });

const fmtMoney = (n) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(Number(n) || 0);

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

const toYmd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const CommissionTracker = () => {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);
  const navigate = useNavigate();

  const [view, setView] = useState('agency'); // 'agency' | 'mine'

  // Period model: the financial year is the default so the board starts fresh
  // ($0) each new FY. Users can flick to a previous FY, a quick trailing window
  // (1W/2W/1M/3M/6M/12M), or a custom date range.
  const currentFY = fyStartYear(new Date());
  const [rangeMode, setRangeMode] = useState('fy'); // 'fy' | '1w' | '2w' | '1m' | '3m' | '6m' | '12m' | 'custom'
  const [fyYear, setFyYear] = useState(currentFY);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const [allGoals, setAllGoals] = useState([]);
  const [currentGoal, setCurrentGoal] = useState(null);
  const [agencyStats, setAgencyStats] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [leaderboardFallback, setLeaderboardFallback] = useState([]);
  const [metricKey, setMetricKey] = useState('sales_closed');
  const [loading, setLoading] = useState(true);

  // "My stats" agent switcher (admin only). null = the signed-in user.
  const [statsAgentId, setStatsAgentId] = useState(null);
  const [agents, setAgents] = useState([]);
  // True while the period-scoped fetches (leaderboard, settlements, office
  // aggregates) are in flight — drives inline spinners so a slow load doesn't
  // read as "$0 / no activity".
  const [periodLoading, setPeriodLoading] = useState(true);

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  // Financial years offered in the FY dropdown: current + the previous four.
  const fyOptions = useMemo(() => {
    const arr = [];
    for (let y = currentFY; y >= currentFY - 4; y--) arr.push(y);
    return arr;
  }, [currentFY]);

  // Resolve the active date window (YYYY-MM-DD) sent to every period-scoped API.
  const periodRange = useMemo(() => {
    if (rangeMode === 'fy') return fyRange(fyYear);
    if (rangeMode === 'custom') {
      if (customStart && customEnd) return { start_date: customStart, end_date: customEnd };
      return fyRange(currentFY); // custom not fully picked yet — fall back to current FY
    }
    const end = new Date();
    const quick = QUICK_RANGES.find((r) => r.key === rangeMode) || {};
    // Week windows (1W/2W) are exact trailing days ending today — the last 7 / 14
    // days inclusive, with no month snapping.
    if (quick.days) {
      const start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - (quick.days - 1));
      return { start_date: toYmd(start), end_date: toYmd(end) };
    }
    const monthsBack = quick.months || 3;
    // Snap the start to the 1st of that month so "1M" covers all of last month
    // (not a mid-month trailing 30 days) and stays aligned with a young FY.
    const start = new Date(end.getFullYear(), end.getMonth() - monthsBack, 1);
    return { start_date: toYmd(start), end_date: toYmd(end) };
  }, [rangeMode, fyYear, customStart, customEnd, currentFY]);

  // Human label for the active period — passed to the stat pages for their header.
  const periodLabel = useMemo(() => {
    if (rangeMode === 'fy') return fyLabel(fyYear);
    if (rangeMode === 'custom') return customStart && customEnd ? `${customStart} to ${customEnd}` : fyLabel(currentFY);
    const r = QUICK_RANGES.find((x) => x.key === rangeMode);
    return r ? `Last ${r.label}` : '';
  }, [rangeMode, fyYear, customStart, customEnd, currentFY]);

  // Open a dedicated stat page for a leaderboard figure. Each metric has its own
  // page (title + Back button); the period + optional agent scope travel in the
  // URL so the page's list matches exactly what the card counted.
  const openStat = useCallback(
    (metric, opts = {}) => {
      const sp = new URLSearchParams();
      sp.set('start_date', periodRange.start_date);
      sp.set('end_date', periodRange.end_date);
      if (periodLabel) sp.set('period', periodLabel);
      if (opts.title) sp.set('title', opts.title);
      if (opts.agent != null) sp.set('agent', String(opts.agent));
      if (opts.agentName) sp.set('agent_name', opts.agentName);
      navigate(`/leaderboard/${metric}?${sp.toString()}`);
    },
    [navigate, periodRange, periodLabel]
  );

  // The signed-in agent's own goals (or, for admins, the selected agent's) drive
  // the "My stats" view. Reloads when the admin switches agent.
  const loadGoals = useCallback(
    async (agentId) => {
      setLoading(true);
      const safety = setTimeout(() => setLoading(false), 12000);
      try {
        const params = agentId ? { user_id: agentId } : {};
        const goalsRes = await cachedApiCall(
          cacheKeys.kpi(`${agentId || 'me'}-${currentMonth}-${currentYear}`),
          () => getMonthlyGoals(params),
          10 * 60 * 1000
        );
        const goals = Array.isArray(goalsRes.data) ? goalsRes.data : goalsRes.data?.results || [];
        setAllGoals(goals);
        const cg = goals.find((g) => g.month === currentMonth && g.year === currentYear) || null;
        setCurrentGoal(cg);
      } catch (e) {
        toast.error('Failed to load stats');
      } finally {
        clearTimeout(safety);
        setLoading(false);
      }
    },
    [currentMonth, currentYear]
  );

  useEffect(() => {
    loadGoals(statsAgentId);
  }, [statsAgentId, loadGoals]);

  // Agent roster — seeds the leaderboard so every agent is always listed (with
  // 0s for a fresh period) and powers the admin "My stats" switcher.
  useEffect(() => {
    getAgents({ page_size: 100 })
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data?.results || [];
        setAgents(list);
      })
      .catch(() => {});
  }, []);

  // Period-scoped data (leaderboard, settlements, office aggregates) — reloads
  // whenever the selected date window changes. `periodLoading` stays true until
  // all three resolve so the UI shows spinners instead of premature zeros.
  useEffect(() => {
    let cancelled = false;
    setPeriodLoading(true);
    Promise.allSettled([
      cachedApiCall(
        `leaderboard-commission-${periodRange.start_date}-${periodRange.end_date}`,
        () => getCommissionLeaderboard(periodRange),
        5 * 60 * 1000
      ).then((r) => {
        if (!cancelled) setLeaderboardFallback(r.data || []);
      }),
      cachedApiCall(
        `leaderboard-settlements-${periodRange.start_date}-${periodRange.end_date}`,
        () => getSoldPropertyCommissions(periodRange),
        5 * 60 * 1000
      ).then((r) => {
        if (!cancelled) setSettlements(r.data || []);
      }),
      cachedApiCall(
        `leaderboard-dashboard-${periodRange.start_date}-${periodRange.end_date}`,
        () => getDashboardStats(periodRange),
        5 * 60 * 1000
      ).then((r) => {
        if (!cancelled) setAgencyStats(r.data);
      }),
    ]).finally(() => {
      if (!cancelled) setPeriodLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [periodRange]);

  const goalVal = (goal, field) => Number(goal?.[field] || 0);

  // ---- Agency leaderboard table rows -------------------------------------
  // The period commission leaderboard carries each agent's appraisals, on/off-
  // market listings, sales and commission for the window (listings attributed
  // to the property's listing agent = 1st contact). We seed a row for EVERY
  // agent so the board stays populated — figures simply reset to 0 for a fresh
  // period (e.g. the start of a new financial year) rather than showing nothing.
  const leaderRows = useMemo(() => {
    const byId = {};
    (leaderboardFallback || []).forEach((e) => {
      if (e && e.agent_id != null) byId[e.agent_id] = e;
    });

    const toRow = (agent_id, name, e) => ({
      agent_id,
      name: name || 'Unknown',
      appraisals: Number(e?.appraisals || 0),
      onMarket: Number(e?.on_market_listings || 0),
      offMarket: Number(e?.off_market_listings || 0),
      sales: Number(e?.commission_count || 0),
      commission: Number(e?.total_commission || 0),
    });

    // Every rostered agent (plus any admin/principal who actually has activity).
    // Dev accounts (media and tech team) are never rostered here. The backend also
    // leaves them out of the leaderboard payload.
    const rosterRows = (agents || [])
      .filter((a) => a && a.id != null && a.role !== 'dev' && (a.role === 'agent' || byId[a.id]))
      .map((a) => toRow(a.id, a.name, byId[a.id]));

    // Any agent with activity who isn't in the roster (e.g. has since left).
    const rosterIds = new Set(rosterRows.map((r) => r.agent_id));
    const extraRows = (leaderboardFallback || [])
      .filter((e) => e && e.agent_id != null && !rosterIds.has(e.agent_id))
      .map((e) => toRow(e.agent_id, e.agent_name, e));

    return [...rosterRows, ...extraRows].sort(
      (a, b) =>
        b.commission - a.commission ||
        b.onMarket + b.offMarket - (a.onMarket + a.offMarket) ||
        (a.name || '').localeCompare(b.name || '')
    );
  }, [leaderboardFallback, agents]);

  // ---- Agency outcome cards + results ------------------------------------
  const agency = useMemo(() => {
    const pipeline = agencyStats?.pipeline;
    const props = agencyStats?.properties;
    const lb = leaderboardFallback || [];

    const commission = lb.reduce((s, e) => s + Number(e.total_commission || 0), 0);
    const salesClosed = lb.reduce((s, e) => s + Number(e.commission_count || 0), 0);
    const onMarket = lb.reduce((s, e) => s + Number(e.on_market_listings || 0), 0);
    const offMarket = lb.reduce((s, e) => s + Number(e.off_market_listings || 0), 0);

    return {
      leads: pipeline?.selling_leads ?? 0,
      listingsWon: onMarket + offMarket,
      salesClosed,
      commission,
      results: {
        offMarket,
        onMarket,
        live: props?.active_listings ?? 0,
        sold: settlements.length,
        lost: props?.lost_listings ?? 0,
      },
    };
  }, [agencyStats, leaderboardFallback, settlements]);

  const unpaidSettlements = useMemo(
    () => settlements.filter((s) => s && !s.settlement_date).length,
    [settlements]
  );

  // ---- My stats ----------------------------------------------------------
  const mine = useMemo(() => {
    const g = currentGoal;
    const target = goalVal(g, 'commission_target') || 1000000;
    // GCI earned comes from the LIVE commission leaderboard (the same source the
    // ranking table uses), NOT MonthlyGoal.current_commission — that field is
    // never synced from actual sales, so it drifted to about half the leaderboard
    // figure (ticket 12688817638: "GCI on the target is half the leaderboard").
    // Match the leaderboard by reading this agent's row; fall back to the goal
    // field only if there is no leaderboard row for the selected period.
    const myId = g?.user;
    const lbRow = (leaderboardFallback || []).find(
      (e) => e && myId != null && Number(e.agent_id) === Number(myId)
    );
    const earned = lbRow
      ? Number(lbRow.total_commission || 0)
      : goalVal(g, 'current_commission');
    const pct = target > 0 ? Math.round((earned / target) * 100) : 0;
    return {
      appraisals: goalVal(g, 'current_appraisal'),
      listings: goalVal(g, 'current_listing'),
      sales: goalVal(g, 'current_sales'),
      commission: earned,
      gciEarned: earned,
      gciTarget: target,
      gciPct: pct,
    };
  }, [currentGoal, leaderboardFallback]);

  const progressCards = useMemo(
    () =>
      METRICS.map((m) => {
        const [cf, tf] = m.fields;
        const current = goalVal(currentGoal, cf);
        const target = goalVal(currentGoal, tf);
        const pct = target > 0 ? Math.round((current / target) * 100) : 0;
        const color = pct >= 100 ? C.green : pct >= 50 ? C.amber : C.red;
        return { ...m, current, target, pct, color };
      }),
    [currentGoal]
  );

  // Historical trend for the selected metric over the chosen window.
  // FY mode → the 12 calendar months of that financial year (Jul→Jun);
  // custom → the trailing 12 months; quick ranges → their trailing window.
  const trend = useMemo(() => {
    const m = METRICS.find((x) => x.id === metricKey) || METRICS[0];
    const [cf, tf] = m.fields;

    // Build the list of {year, month} the chart should cover, oldest first.
    let months = [];
    if (rangeMode === 'fy') {
      for (let i = 0; i < 12; i++) {
        const d = new Date(fyYear, 6 + i, 1); // Jul(fyYear) .. Jun(fyYear+1)
        months.push(d);
      }
    } else {
      const monthsBack =
        rangeMode === 'custom'
          ? 12
          : (QUICK_RANGES.find((r) => r.key === rangeMode) || {}).months || 6;
      for (let i = monthsBack - 1; i >= 0; i--) {
        months.push(new Date(currentYear, currentMonth - 1 - i, 1));
      }
    }

    const rows = months.map((d) => {
      const mm = d.getMonth() + 1;
      const yy = d.getFullYear();
      const g = allGoals.find((x) => x.month === mm && x.year === yy);
      return {
        name: d.toLocaleString('default', { month: 'short' }),
        value: g ? Number(g[cf] || 0) : 0,
        goal: g ? Number(g[tf] || 0) : goalVal(currentGoal, tf),
      };
    });
    const total = rows.reduce((s, r) => s + r.value, 0);
    return { rows, total, unit: m.unit, label: m.label };
  }, [metricKey, rangeMode, fyYear, allGoals, currentGoal, currentMonth, currentYear]);

  const refresh = useCallback(() => {
    apiCache.invalidate(cacheKeys.kpi(`${statsAgentId || 'me'}-${currentMonth}-${currentYear}`));
    apiCache.invalidate(`leaderboard-commission-${periodRange.start_date}-${periodRange.end_date}`);
    apiCache.invalidate(`leaderboard-settlements-${periodRange.start_date}-${periodRange.end_date}`);
    apiCache.invalidate(`leaderboard-dashboard-${periodRange.start_date}-${periodRange.end_date}`);
    loadGoals(statsAgentId);
    getCommissionLeaderboard(periodRange)
      .then((r) => setLeaderboardFallback(r.data || []))
      .catch(() => {});
    getSoldPropertyCommissions(periodRange)
      .then((r) => setSettlements(r.data || []))
      .catch(() => {});
    getDashboardStats(periodRange)
      .then((r) => setAgencyStats(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsAgentId, currentMonth, currentYear, periodRange.start_date, periodRange.end_date, loadGoals]);

  // ---- Small presentational helpers --------------------------------------
  const pill = (active) => ({
    border: 'none',
    background: active ? C.teal : 'transparent',
    color: active ? C.darkGreen : C.textSecondary,
    padding: '6px 14px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  });

  // Whose "My stats" we're showing (self unless an admin picked another agent).
  const viewingSelf = !statsAgentId;
  const statsAgentName = viewingSelf
    ? user?.name || 'You'
    : agents.find((a) => String(a.id) === String(statsAgentId))?.name || 'This agent';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: C.teal }} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '100%', padding: '1rem' }}>
      {/* Header: view toggle + date range */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-3.5">
          <span style={{ fontSize: 20, fontWeight: 500, letterSpacing: '0.02em', color: C.textPrimary }}>
            COLLINGS
          </span>
          <div
            style={{ display: 'inline-flex', background: C.surface1, borderRadius: 999, padding: 3, border: `0.5px solid ${C.border}` }}
          >
            <button style={pill(view === 'agency')} onClick={() => setView('agency')}>Agency leaderboard</button>
            <button style={pill(view === 'mine')} onClick={() => setView('mine')}>My stats</button>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            style={{ display: 'inline-flex', flexWrap: 'wrap', justifyContent: 'flex-end', background: C.surface1, borderRadius: 999, padding: 3, border: `0.5px solid ${C.border}` }}
          >
            {QUICK_RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRangeMode(r.key)}
                style={{ ...pill(rangeMode === r.key), padding: '5px 10px', fontSize: 11 }}
              >
                {r.label}
              </button>
            ))}
            <button
              onClick={() => setRangeMode('fy')}
              style={{ ...pill(rangeMode === 'fy'), padding: '5px 10px', fontSize: 11 }}
            >
              FY
            </button>
            <button
              onClick={() => setRangeMode('custom')}
              style={{ ...pill(rangeMode === 'custom'), padding: '5px 10px', fontSize: 11 }}
            >
              Custom
            </button>
          </div>

          {rangeMode === 'fy' && (
            <select
              value={fyYear}
              onChange={(e) => setFyYear(Number(e.target.value))}
              style={{ fontSize: 12, fontWeight: 600, color: C.darkGreen, padding: '5px 8px', borderRadius: 8, border: `0.5px solid ${C.border}`, background: 'var(--c-panel)', cursor: 'pointer' }}
            >
              {fyOptions.map((y) => (
                <option key={y} value={y}>
                  {fyLabel(y)}{y === currentFY ? ' · Current' : ''}
                </option>
              ))}
            </select>
          )}

          {rangeMode === 'custom' && (
            <div className="flex items-center gap-1">
              <input
                type="date"
                value={customStart}
                max={customEnd || undefined}
                onChange={(e) => setCustomStart(e.target.value)}
                style={{ fontSize: 12, padding: '4px 6px', borderRadius: 8, border: `0.5px solid ${C.border}` }}
              />
              <span style={{ fontSize: 11, color: C.textMuted }}>to</span>
              <input
                type="date"
                value={customEnd}
                min={customStart || undefined}
                onChange={(e) => setCustomEnd(e.target.value)}
                style={{ fontSize: 12, padding: '4px 6px', borderRadius: 8, border: `0.5px solid ${C.border}` }}
              />
            </div>
          )}
        </div>
      </div>

      {/* ============================ AGENCY VIEW ============================ */}
      {view === 'agency' && (
        <div>
          {/* Outcome cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-3.5">
            <StatCard
              label="SELLING LEADS"
              value={agency.leads}
              loading={periodLoading}
              onClick={() => openStat('leads', { title: 'Selling Leads' })}
              hint="View selling leads"
            />
            <StatCard
              label="LISTINGS WON"
              value={agency.listingsWon}
              loading={periodLoading}
              onClick={() => openStat('listings', { title: 'Listings Won' })}
              hint="View listings"
            />
            <StatCard
              label="SALES CLOSED"
              value={agency.salesClosed}
              loading={periodLoading}
              onClick={() => openStat('sold', { title: 'Sales Closed' })}
              hint="View sold"
            />
            <StatCard
              label="COMMISSION EARNED"
              value={fmtMoney(agency.commission)}
              accent
              loading={periodLoading}
              onClick={() => openStat('commission', { title: 'Commission Earned' })}
              hint="View sold"
            />
          </div>

          {/* Agent leaderboard */}
          <div className="mb-3.5">
            <div className="flex items-center justify-between mb-2">
              <span style={{ fontSize: 12, fontWeight: 600, color: C.textSecondary, letterSpacing: '0.02em' }}>
                AGENT LEADERBOARD
              </span>
              <span style={{ fontSize: 11, color: C.textMuted }}>
                Ranked by commission, this period
              </span>
            </div>
            <div style={{ border: `0.5px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
                <thead>
                  <tr style={{ background: C.teal }}>
                    <th style={thL(28)}>Agent</th>
                    <th style={thR(16)}>Appraisals</th>
                    <th style={thR(13)}>On Mkt</th>
                    <th style={thR(13)}>Off Mkt</th>
                    <th style={thR(12)}>Sales</th>
                    <th style={{ ...thR(18), paddingRight: 12 }}>Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {periodLoading ? (
                    <tr>
                      <td colSpan={6} style={{ padding: '28px', textAlign: 'center' }}>
                        <Spinner />
                      </td>
                    </tr>
                  ) : leaderRows.length ? (
                    leaderRows.map((a, i) => (
                      <tr
                        key={a.agent_id || i}
                        style={{ borderTop: `0.5px solid ${C.border}`, background: i % 2 === 1 ? C.surface1 : undefined }}
                      >
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: C.textPrimary }}>
                          {i + 1}. {a.name}{a.agent_id === user?.id ? ' (You)' : ''}
                        </td>
                        <td style={tdR}>
                          <NumCell value={a.appraisals} onClick={() => openStat('appraisals', { agent: a.agent_id, agentName: a.name, title: 'Appraisals' })} />
                        </td>
                        <td style={tdR}>
                          <NumCell value={a.onMarket} onClick={() => openStat('on_market', { agent: a.agent_id, agentName: a.name, title: 'On-Market Listings' })} />
                        </td>
                        <td style={tdR}>
                          <NumCell value={a.offMarket} onClick={() => openStat('off_market', { agent: a.agent_id, agentName: a.name, title: 'Off-Market Listings' })} />
                        </td>
                        <td style={tdR}>
                          <NumCell value={a.sales} onClick={() => openStat('sold', { agent: a.agent_id, agentName: a.name, title: 'Sold' })} />
                        </td>
                        <td style={{ ...tdR, paddingRight: 12, fontWeight: 600, color: C.green }}>
                          <NumCell
                            value={fmtMoney(a.commission)}
                            active={a.commission > 0}
                            color={C.green}
                            onClick={() => openStat('commission', { agent: a.agent_id, agentName: a.name, title: 'Commission-Earning Sales' })}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} style={{ padding: '20px', textAlign: 'center', color: C.textMuted, fontSize: 13 }}>
                        No agent activity yet for this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Results + Settlements */}
          <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 1.3fr' }}>
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, color: C.textSecondary, letterSpacing: '0.02em', margin: '0 0 8px' }}>
                RESULTS
              </p>
              <div className="flex flex-col gap-1.5">
                <ResultRow
                  label="Off-market listed"
                  value={agency.results.offMarket}
                  loading={periodLoading}
                  onClick={() => openStat('off_market', { title: 'Off-Market Listed' })}
                />
                <ResultRow
                  label="On-market listed"
                  value={agency.results.onMarket}
                  loading={periodLoading}
                  onClick={() => openStat('on_market', { title: 'On-Market Listed' })}
                />
                <ResultRow
                  label="Live"
                  value={agency.results.live}
                  loading={periodLoading}
                  onClick={() => openStat('live', { title: 'Live Listings' })}
                />
                <ResultRow
                  label="Sold"
                  value={agency.results.sold}
                  highlight
                  loading={periodLoading}
                  onClick={() => openStat('sold', { title: 'Sold' })}
                />
                <ResultRow
                  label="Lost (withdrawn)"
                  value={agency.results.lost}
                  danger
                  loading={periodLoading}
                  onClick={() => openStat('lost', { title: 'Lost (Withdrawn)' })}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span style={{ fontSize: 12, fontWeight: 600, color: C.textSecondary, letterSpacing: '0.02em' }}>
                  RECENT SETTLEMENTS
                </span>
                {unpaidSettlements > 0 && (
                  <span style={{ fontSize: 11, color: C.red, background: C.redLight, padding: '2px 8px', borderRadius: 999 }}>
                    {unpaidSettlements} pending
                  </span>
                )}
              </div>
              <div style={{ border: `0.5px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{ background: C.surface1 }}>
                      <th style={{ ...thL(48), color: C.textSecondary, fontSize: 11 }}>Property</th>
                      <th style={{ ...thR(24), color: C.textSecondary, fontSize: 11 }}>Commission</th>
                      <th style={{ ...thR(28), color: C.textSecondary, fontSize: 11, paddingRight: 10 }}>Settles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periodLoading ? (
                      <tr>
                        <td colSpan={3} style={{ padding: '24px', textAlign: 'center' }}>
                          <Spinner />
                        </td>
                      </tr>
                    ) : settlements.length ? (
                      settlements.slice(0, 6).map((s, i) => (
                        <tr
                          key={s.property_id || i}
                          style={{ borderTop: `0.5px solid ${C.border}`, background: i % 2 === 1 ? C.surface1 : undefined }}
                        >
                          <td style={{ padding: '6px 10px', color: C.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.property_address || s.property || '—'}
                          </td>
                          <td style={{ ...tdR, padding: '6px 8px', color: C.green, fontWeight: 600 }}>
                            {fmtMoney(s.amount)}
                          </td>
                          <td style={{ ...tdR, padding: '6px 10px 6px 8px', color: C.textSecondary }}>
                            {fmtDate(s.settlement_date)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} style={{ padding: '16px', textAlign: 'center', color: C.textMuted }}>
                          No settlements this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Portal sign-ups by agent — the team competition board (Monday
              13067959735). Same data as the analytics "Real Estate Agents"
              drill-down: QR sign-ups plus sign-ups from links the agent sent
              by email/SMS, on this page's own period. */}
          <div className="mt-3.5">
            <AgentSignupPanel
              startDate={periodRange.start_date}
              endDate={periodRange.end_date}
            />
          </div>
        </div>
      )}

      {/* ============================= MY STATS ============================= */}
      {view === 'mine' && (
        <div>
          <div className="flex items-center gap-2.5 mb-3.5 flex-wrap">
            {isAdmin ? (
              <select
                value={statsAgentId || ''}
                onChange={(e) => setStatsAgentId(e.target.value || null)}
                style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary, background: C.surface1, padding: '6px 12px', borderRadius: 8, border: `0.5px solid ${C.border}`, cursor: 'pointer' }}
              >
                <option value="">{user?.name || 'Me'} (You)</option>
                {agents
                  // Dev (media and tech) accounts are not sales agents, they have
                  // no sales figures, so they stay off the leaderboard entirely.
                  .filter((a) => a.id !== user?.id && a.role !== 'dev')
                  .map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
              </select>
            ) : (
              <span
                style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary, background: C.surface1, padding: '6px 12px', borderRadius: 8, border: `0.5px solid ${C.border}` }}
              >
                {user?.name || 'You'}
              </span>
            )}
            <span style={{ fontSize: 12, color: C.textMuted }}>
              {viewingSelf ? 'Viewing your results for this period' : `Viewing ${statsAgentName}'s results for this period`}
            </span>
          </div>

          {!currentGoal ? (
            <div
              style={{ background: C.surface1, borderRadius: 12, padding: '28px 16px', textAlign: 'center', color: C.textSecondary, fontSize: 13 }}
            >
              No goals set for {viewingSelf ? 'you' : statsAgentName} in {now.toLocaleString('default', { month: 'long', year: 'numeric' })}.
              {viewingSelf && <> Set your targets on the <strong>KPI &amp; Goals</strong> page to see your stats here.</>}
            </div>
          ) : (
            <>
              {/* Outcome cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-3.5">
                <StatCard label="APPRAISALS" value={mine.appraisals} small />
                <StatCard label="LISTINGS" value={mine.listings} small />
                <StatCard label="SALES" value={mine.sales} small />
                <StatCard label="COMMISSION" value={fmtMoney(mine.commission)} accent small />
              </div>

              {/* GCI target bar */}
              <div
                className="mb-3.5 flex items-center justify-between flex-wrap gap-2.5"
                style={{ background: C.greenLight, borderRadius: 12, padding: '14px 16px' }}
              >
                <div>
                  <p style={{ fontSize: 12, color: C.green, margin: '0 0 2px', letterSpacing: '0.02em' }}>GCI TARGET</p>
                  <p style={{ fontSize: 13, color: 'var(--c-on-money)', margin: 0 }}>
                    {fmtMoney(mine.gciEarned)} earned of {fmtMoney(mine.gciTarget)} goal
                  </p>
                </div>
                <div style={{ flex: 1, minWidth: 160, maxWidth: 280 }}>
                  <div style={{ background: C.greenBar, borderRadius: 999, height: 8, overflow: 'hidden' }}>
                    <div style={{ background: C.green, height: '100%', width: `${Math.min(mine.gciPct, 100)}%` }} />
                  </div>
                </div>
                <span style={{ fontSize: 18, fontWeight: 600, color: C.darkGreen }}>{mine.gciPct}%</span>
              </div>

              {/* Current progress grid */}
              <p style={{ fontSize: 12, fontWeight: 600, color: C.textSecondary, letterSpacing: '0.02em', margin: '0 0 8px' }}>
                CURRENT PROGRESS
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 mb-4">
                {progressCards.map((m) => (
                  <div key={m.id} style={{ background: C.surface1, borderRadius: 10, padding: '10px 12px' }}>
                    <p style={{ fontSize: 11, color: C.textSecondary, margin: '0 0 4px', letterSpacing: '0.02em' }}>
                      {m.label.toUpperCase()}
                      {m.auto && <span style={{ color: C.green, marginLeft: 4 }}>AUTO</span>}
                    </p>
                    <div className="flex items-baseline justify-between">
                      <span style={{ fontSize: 18, fontWeight: 600, color: C.textPrimary }}>
                        {m.current}
                        <span style={{ fontSize: 11, color: C.textMuted }}> /{m.target}</span>
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: m.color }}>{m.pct}%</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Historical trend */}
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <p style={{ fontSize: 12, fontWeight: 600, color: C.textSecondary, letterSpacing: '0.02em', margin: 0 }}>
                  HISTORICAL TREND
                </p>
                <select
                  value={metricKey}
                  onChange={(e) => setMetricKey(e.target.value)}
                  style={{ fontSize: 13, padding: '5px 8px', borderRadius: 8, border: `0.5px solid ${C.border}` }}
                >
                  {METRICS.map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
              </div>
              <p style={{ fontSize: 11, color: C.textMuted, margin: '0 0 6px', textAlign: 'right' }}>
                {trend.total.toLocaleString()} {trend.unit} total over this period
              </p>
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend.rows} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#e1e0d9" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#898781' }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#898781' }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="value" name={trend.label} stroke="#1baf7a" strokeWidth={2} dot={{ r: 4, fill: '#1baf7a' }} />
                    <Line type="monotone" dataKey="goal" name="Goal" stroke="#898781" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      )}

      <div className="flex justify-end mt-4">
        <button
          onClick={refresh}
          style={{ fontSize: 12, color: C.textSecondary, background: 'transparent', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}
        >
          Refresh
        </button>
      </div>
    </div>
  );
};

// ---- Shared table cell styles ----
const thL = (w) => ({ textAlign: 'left', padding: '8px 12px', fontWeight: 500, color: C.darkGreen, width: `${w}%` });
const thR = (w) => ({ textAlign: 'right', padding: '8px 10px', fontWeight: 500, color: C.darkGreen, width: `${w}%` });
const tdR = { padding: '8px 10px', textAlign: 'right', color: C.textSecondary };

// Small inline spinner used wherever period data is still loading.
const Spinner = ({ size = 18 }) => (
  <span
    className="inline-block animate-spin rounded-full align-middle"
    style={{ width: size, height: size, border: `2px solid ${C.border}`, borderBottomColor: C.teal }}
  />
);

const StatCard = ({ label, value, accent = false, small = false, loading = false, onClick, hint }) => {
  const clickable = typeof onClick === 'function';
  return (
    <div
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      title={clickable ? (hint || 'View details') : undefined}
      style={{
        background: accent ? C.greenLight : C.surface1,
        borderRadius: 10,
        padding: '12px 14px',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'box-shadow 120ms, transform 120ms',
        position: 'relative',
      }}
      onMouseEnter={clickable ? (e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; } : undefined}
      onMouseLeave={clickable ? (e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; } : undefined}
    >
      <p style={{ fontSize: 11, color: accent ? C.green : C.textSecondary, margin: '0 0 4px', letterSpacing: '0.02em' }}>
        {label}
      </p>
      {loading ? (
        <div style={{ height: small ? 28 : 30, display: 'flex', alignItems: 'center' }}>
          <Spinner size={20} />
        </div>
      ) : (
        <p style={{ fontSize: small ? 22 : 24, fontWeight: 600, margin: 0, color: accent ? C.darkGreen : C.textPrimary }}>
          {value}
        </p>
      )}
      {clickable && !loading && (
        <span style={{ position: 'absolute', top: 10, right: 12, fontSize: 11, color: C.textMuted }}>›</span>
      )}
    </div>
  );
};

// A numeric leaderboard cell. Clickable (underline on hover) when it has a
// value to drill into; a plain muted "0" otherwise so dead links don't tease.
const NumCell = ({ value, onClick, active, color }) => {
  const isActive = active !== undefined ? active : Number(value) > 0;
  if (!isActive || typeof onClick !== 'function') {
    return <span style={{ color: color || C.textMuted }}>{value}</span>;
  }
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        font: 'inherit', color: color || C.teal, textDecoration: 'underline',
        textDecorationColor: 'rgba(0,0,0,0.15)', textUnderlineOffset: 2,
      }}
      title="View listings"
    >
      {value}
    </button>
  );
};

const ResultRow = ({ label, value, highlight = false, danger = false, loading = false, onClick }) => {
  const bg = danger ? C.redLight : highlight ? '#eaf3de' : C.surface1;
  const fg = danger ? C.red : highlight ? '#173404' : C.textPrimary;
  const clickable = typeof onClick === 'function';
  return (
    <div
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      title={clickable ? `View ${label.toLowerCase()}` : undefined}
      className="flex items-center justify-between"
      style={{ background: bg, borderRadius: 10, padding: '9px 12px', cursor: clickable ? 'pointer' : 'default' }}
      onMouseEnter={clickable ? (e) => { e.currentTarget.style.filter = 'brightness(0.97)'; } : undefined}
      onMouseLeave={clickable ? (e) => { e.currentTarget.style.filter = 'none'; } : undefined}
    >
      <span style={{ fontSize: 13, color: fg, display: 'flex', alignItems: 'center', gap: 6 }}>
        {label}
      </span>
      {loading ? (
        <Spinner size={14} />
      ) : (
        <span style={{ fontSize: 15, fontWeight: 600, color: fg, display: 'flex', alignItems: 'center', gap: 4 }}>
          {value}
          {clickable && <span style={{ fontSize: 12, color: C.textMuted, fontWeight: 400 }}>›</span>}
        </span>
      )}
    </div>
  );
};

export default React.memo(CommissionTracker);
